<script setup lang="ts">
  import { useSubAppStore } from '@/store/sub-app.store'
  import {
    ConfirmationDialog,
    Modal,
    UiButton,
    useToastStore,
  } from '@logistiek-zonder-papier/lzp-library'
  import { isAxiosError } from 'axios'
  import type { UUID } from 'node:crypto'
  import { ref, useTemplateRef, watch } from 'vue'

  const subAppStore = useSubAppStore()
  const toastStore = useToastStore()

  const { subAppId } = defineProps<{ subAppId: UUID }>()

  const show = defineModel<boolean>('show', { default: false })
  const hasTranslations = defineModel<boolean>('hasTranslations', { default: false })

  const fileInput = useTemplateRef<HTMLInputElement>('fileInput')
  const selectedFile = ref<File | null>(null)
  const isUploading = ref(false)
  const isDownloading = ref(false)
  const showDeleteConfirmation = ref(false)

  function onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement
    selectedFile.value = input.files?.[0] ?? null
  }

  async function upload() {
    if (!selectedFile.value) return

    try {
      JSON.parse(await selectedFile.value.text())
    } catch {
      toastStore.error('File contains invalid JSON.')
      return
    }

    isUploading.value = true
    try {
      await subAppStore.uploadTranslations(subAppId, selectedFile.value)

      hasTranslations.value = true
      toastStore.success('Translations uploaded.')
      show.value = false
    } catch (error) {
      if (isAxiosError(error)) {
        toastStore.error(error.response?.data?.message ?? 'Could not upload translations.')
      } else {
        throw error
      }
    } finally {
      isUploading.value = false
    }
  }

  async function download() {
    isDownloading.value = true
    try {
      const { blob, fileName } = await subAppStore.downloadTranslations(subAppId)

      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      link.click()
      URL.revokeObjectURL(blobUrl)
    } catch (error) {
      if (isAxiosError(error)) {
        toastStore.error(
          error.response?.status === 404
            ? 'No translations uploaded yet.'
            : 'Could not download translations.'
        )
      } else {
        throw error
      }
    } finally {
      isDownloading.value = false
    }
  }

  async function deleteTranslations() {
    try {
      await subAppStore.deleteTranslations(subAppId)

      hasTranslations.value = false
      toastStore.success('Translations deleted.')
    } catch (error) {
      if (isAxiosError(error)) {
        toastStore.error('Could not delete translations.')
      } else {
        throw error
      }
    } finally {
      showDeleteConfirmation.value = false
    }
  }

  watch(show, isShown => {
    if (isShown) return

    selectedFile.value = null
    if (fileInput.value) fileInput.value.value = ''
  })
</script>

<template>
  <Modal v-model:show="show">
    <template #header>
      <h3>Translations</h3>
    </template>

    <div class="translations">
      <div v-if="hasTranslations" class="current-file">
        <UiButton
          label="Download current translations"
          icon="download"
          :disabled="isDownloading"
          @click="download"
        />
        <UiButton icon="delete" variant="negative" @click="showDeleteConfirmation = true" />
      </div>
      <p v-else class="no-translations">No translations uploaded yet.</p>

      <div class="file-select">
        <input
          ref="fileInput"
          class="file-input"
          type="file"
          accept=".json,application/json"
          @change="onFileSelected"
        />
        <UiButton label="Choose file" @click="fileInput?.click()" />
        <span class="file-name">{{ selectedFile?.name ?? 'No file chosen' }}</span>
      </div>
    </div>

    <template #footer="{ close }">
      <UiButton variant="default" label="Annuleren" @click="close" />
      <UiButton
        variant="prominent"
        label="Uploaden"
        :disabled="!selectedFile || isUploading"
        @click="upload"
      />
    </template>
  </Modal>
  <ConfirmationDialog
    v-model:show="showDeleteConfirmation"
    title="Are you sure you want to delete the current translations?"
    body="This action cannot be undone."
    confirmButtonText="Delete"
    confirmButtonColor="negative"
    :confirm="deleteTranslations"
  />
</template>

<style lang="css" scoped>
  .translations {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-m);
  }

  .current-file {
    display: flex;
    gap: var(--spacing-s);
  }

  .no-translations {
    color: var(--colours-grey-4);
  }

  .file-select {
    display: flex;
    align-items: center;
    gap: var(--spacing-s);
  }

  .file-input {
    display: none;
  }

  .file-name {
    color: var(--colours-grey-4);
  }
</style>
